// =============================================================================
// components/ui/LoadingState.tsx — shared loading + slow-load fallback surface.
//
// Purely presentational: a centered ActivityIndicator (+ optional message) while
// a load is in flight. If `timeoutMs` elapses without the component unmounting,
// it flips to a "Taking longer than expected" state inside a GlassCard with a
// Retry button. Retry calls `onRetry` (which should re-trigger the caller's load)
// AND resets this component's own timer, so the spinner returns and the timeout
// can fire again on a subsequent slow load.
//
// Owns no data-fetching itself — the parent keeps this mounted while loading and
// unmounts it on success/error. The only side effect is a setTimeout cleared on
// unmount. Reads light-theme tokens by default; `theme="dark"` opts into the
// dark glass + text tokens (voice/call surfaces).
// =============================================================================
import React, { useEffect, useState } from 'react';
import { ActivityIndicator, StyleSheet, Text, TouchableOpacity, View } from 'react-native';
import GlassCard from '../glass/GlassCard';
import { colorTokens, colors, spacing, typographyScale } from '../../constants/theme';

export interface LoadingStateProps {
  /** Optional label shown under the spinner. */
  message?: string;
  /** Ms before flipping to the slow-load fallback. Default 8000. */
  timeoutMs?: number;
  /** Re-trigger the caller's load; also resets this component's timer. */
  onRetry?: () => void;
  /** Token palette. 'light' (lavender, default) or 'dark' (voice/call). */
  theme?: 'light' | 'dark';
}

export default function LoadingState({
  message,
  timeoutMs = 8000,
  onRetry,
  theme = 'light',
}: LoadingStateProps) {
  const t = colorTokens[theme];
  const [slow, setSlow] = useState(false);

  // Slow-load timer. Re-arms whenever timeoutMs changes or Retry resets `slow`
  // back to false. Cleared on unmount so no state update fires after teardown.
  useEffect(() => {
    if (slow) return;
    const id = setTimeout(() => setSlow(true), timeoutMs);
    return () => clearTimeout(id);
  }, [timeoutMs, slow]);

  const handleRetry = () => {
    setSlow(false); // back to the spinner + re-arm the timer via the effect
    onRetry?.();
  };

  if (slow) {
    return (
      <View style={styles.center}>
        <GlassCard mode={theme} style={styles.card} contentStyle={styles.cardContent}>
          <Text style={[styles.title, { color: t.text }]}>Taking longer than expected</Text>
          {onRetry ? (
            <TouchableOpacity
              accessibilityRole="button"
              accessibilityLabel="Retry"
              onPress={handleRetry}
              activeOpacity={0.85}
              style={[styles.retryBtn, { backgroundColor: t.primary }]}
            >
              <Text style={styles.retryLabel}>Retry</Text>
            </TouchableOpacity>
          ) : null}
        </GlassCard>
      </View>
    );
  }

  return (
    <View style={styles.center}>
      <ActivityIndicator color={colors.primary} />
      {message ? <Text style={[styles.message, { color: t.textMuted }]}>{message}</Text> : null}
    </View>
  );
}

const styles = StyleSheet.create({
  center: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    padding: spacing.xl,
  },
  message: {
    ...typographyScale.body,
    marginTop: spacing.md,
    textAlign: 'center',
  },
  card: {
    alignItems: 'stretch',
    maxWidth: 320,
  },
  cardContent: {
    alignItems: 'center',
  },
  title: {
    ...typographyScale.h3,
    textAlign: 'center',
  },
  retryBtn: {
    marginTop: spacing.lg,
    paddingVertical: spacing.md,
    paddingHorizontal: spacing.xxl,
    borderRadius: 9999,
  },
  retryLabel: {
    ...typographyScale.body,
    color: colors.white,
    fontWeight: '700',
  },
});
